/**
 * The `dlp.labels` clause of the workspace policy: Microsoft Purview
 * sensitivity labels mapped onto the one sensitivity enum the workbench, the
 * node and AIOE share (`public | internal | confidential | restricted`), and
 * where content of each level may be sent
 * (docs/plans/2026-10-purview-sensitivity-labels.md sections 4-6).
 *
 * AIOE publishes the clause inside the policy it already pushes; a person may
 * also write it by hand into `.mojoup/policy.json` (a taxonomy typed in for
 * testing, or for an organisation without AIOE). The shape is mirrored in
 * mju-aioe `packages/shared/src/schemas/policy.ts` under `dlp.labels`: keep
 * the two in step. Every field but `taxonomy` is optional, so an older
 * publisher's clause still parses.
 *
 *   taxonomy       the tenant's labels: GUID, name, the sensitivity it maps
 *                  to, the parent of a sub-label, and whether it encrypts
 *                  (a protected label is never stamped onto an artifact).
 *   routes         per sensitivity, the route classes content may go to:
 *                  `local` (this machine: the bundled runner, Ollama, LM
 *                  Studio), `organisation` (the organisation's gateway or a
 *                  node's inference), `hosted` (a vendor's cloud). Left out,
 *                  {@link DEFAULT_LABEL_ROUTES}: Confidential never goes to a
 *                  hosted vendor, Restricted never leaves the machine.
 *   unknownLabel   the sensitivity of a label the taxonomy does not list
 *                  (default confidential).
 *   tenantId       the Entra tenant the taxonomy is from; a label whose
 *                  `SiteId` names another tenant is `foreignTenant`
 *                  (default restricted).
 *   organisationProviders  provider ids that count as the organisation's own
 *                  route (its gateway, a node's inference), not a vendor's.
 *   hostedAction   per sensitivity, what happens when content of that level is
 *                  bound for a hosted (public) model provider its routes do
 *                  not allow: `report` (it goes, audited and the person told
 *                  once), `block` (it does not go), `redact` (the marked
 *                  content is removed first). Ignored for a level whose routes
 *                  include `hosted`; organisation and local routes follow the
 *                  routes alone. Left out, {@link DEFAULT_HOSTED_ACTION}:
 *                  Confidential is removed, Restricted is stopped. A CLI on its
 *                  own sign-in never passes through the workbench's proxy, so
 *                  `redact` cannot apply to what it reads and degrades to
 *                  `block` there.
 *
 * A taxonomy entry may carry `markings`: the label's header, footer or
 * watermark text ("CONFIDENTIAL – Contoso internal"). Text that carries one is
 * content at that label's sensitivity, wherever it came from (a prompt, a
 * pasted block, a tool result). AIOE fills them from the tenant; a repository
 * may list them by hand. They match as written (case kept; runs of spaces and
 * the three dashes are interchangeable), so a marking should be the whole
 * line, not a common word.
 *   rules          what the organisation writes against one label, by its id
 *                  or its name (`{ match: "label:Highly Confidential",
 *                  action: "block" }`): stricter or looser than the routes
 *                  its sensitivity has. See {@link labelRuleDecision}.
 *
 * Without the clause nothing about labels is enforced: a repository whose
 * policy names no taxonomy behaves exactly as before.
 */
import { z } from "zod";
export const SENSITIVITIES = ["public", "internal", "confidential", "restricted"];
export const sensitivitySchema = z.enum(SENSITIVITIES);
export const LABEL_ROUTES = ["local", "organisation", "hosted"];
export const labelRouteSchema = z.enum(LABEL_ROUTES);
/** What each sensitivity may reach when the clause names no routes (JP's decision, 7 Oct 2026). */
export const DEFAULT_LABEL_ROUTES = {
    public: ["local", "organisation", "hosted"],
    internal: ["local", "organisation", "hosted"],
    confidential: ["local", "organisation"],
    restricted: ["local"],
};
const guid = z.string().min(1).max(64);
export const taxonomyLabelSchema = z.object({
    id: guid,
    name: z.string().min(1).max(200),
    sensitivity: sensitivitySchema,
    parentId: guid.nullish(),
    /** The label encrypts (Purview protection): content cannot be read, and the workbench never stamps it. */
    hasProtection: z.boolean().optional(),
    /** The label's content markings (header, footer, watermark text): text carrying one is content at this label's sensitivity. */
    markings: z.array(z.string().trim().min(6).max(500)).max(20).optional(),
    /**
     * The tenant the label belongs to, when it is not the clause's own: a partner's label the
     * organisation mapped, or one the AIOE resolved (`resolve-label`). A file whose `SiteId` is
     * that tenant is then not `foreignTenant`.
     */
    tenantId: z.string().max(64).optional(),
});
/** What happens to labelled content bound for a hosted (public) provider its routes exclude. */
export const HOSTED_ACTIONS = ["report", "block", "redact"];
export const hostedActionSchema = z.enum(HOSTED_ACTIONS);
/**
 * The action per level when the clause names none (JP, 8 Oct 2026): Confidential is removed
 * before it reaches a public provider, Restricted is stopped. Public and Internal may go to
 * any model by their routes, so no action applies to them; should a clause take `hosted` off
 * their routes, they are stopped, as before this setting existed.
 */
export const DEFAULT_HOSTED_ACTION = {
    public: "block",
    internal: "block",
    confidential: "redact",
    restricted: "block",
};
/** `label:<id or name>`: what a rule names. The id may carry braces; names compare without case. */
export const LABEL_MATCH_PREFIX = "label:";
export const labelMatchSchema = z
    .string()
    .max(250)
    .refine((m) => m.toLowerCase().startsWith(LABEL_MATCH_PREFIX) && m.slice(LABEL_MATCH_PREFIX.length).trim().length > 0, { message: "a label match reads label:<id or name>" });
/**
 * A rule against one label (phase 2 of the Purview plan): content carrying it, bound for one of
 * `routes` (default: the organisation's gateway and hosted providers — this machine is never
 * refused), is `block`ed, put to the person (`ask`), or `allow`ed whatever its sensitivity's
 * routes say. Of several matching rules the strictest wins.
 */
export const labelRuleSchema = z.object({
    match: labelMatchSchema,
    action: z.enum(["allow", "ask", "block"]),
    routes: z.array(labelRouteSchema).min(1).max(3).optional(),
});
export const sensitivityLabelsPolicySchema = z.object({
    taxonomy: z.array(taxonomyLabelSchema).max(1000).default([]),
    routes: z.partialRecord(sensitivitySchema, z.array(labelRouteSchema).max(3)).optional(),
    unknownLabel: sensitivitySchema.optional(),
    tenantId: z.string().max(64).optional(),
    foreignTenant: sensitivitySchema.optional(),
    organisationProviders: z.array(z.string().min(1).max(100)).max(50).optional(),
    /** When AIOE last read the taxonomy from the tenant (informational). */
    fetchedAt: z.string().max(40).optional(),
    /** Per sensitivity: report, block or redact content bound for a hosted provider its routes exclude. */
    hostedAction: z.partialRecord(sensitivitySchema, hostedActionSchema).optional(),
    rules: z.array(labelRuleSchema).max(200).optional(),
});
const RANK = { public: 0, internal: 1, confidential: 2, restricted: 3 };
export function sensitivityRank(s) {
    return RANK[s];
}
/** The more sensitive of two levels. */
export function higherSensitivity(a, b) {
    return b !== undefined && RANK[b] > RANK[a] ? b : a;
}
/** The routes content of `s` may go to under `clause` (its own routes, else the defaults). */
export function routesFor(s, clause) {
    return clause?.routes?.[s] ?? DEFAULT_LABEL_ROUTES[s];
}
/** May content of `s` go to `route`? `local` always may: nothing leaves the machine. */
export function routeAllowed(s, route, clause) {
    return route === "local" || routesFor(s, clause).includes(route);
}
/** Where content of `s` may go, in the words a person reads. */
export function routeWords(s, clause) {
    const routes = routesFor(s, clause);
    if (routes.includes("hosted"))
        return "may be sent to any model";
    if (routes.includes("organisation"))
        return "may go to your organisation's gateway, not to a hosted provider";
    return "stays on this machine";
}
/** "Confidential" — the level as a word with a capital. */
export function sensitivityWord(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
}
/** What a request to a hosted provider does with content of `s`: `allow` when its routes include `hosted`, else the clause's action. */
export function hostedActionFor(s, clause) {
    if (routesFor(s, clause).includes("hosted"))
        return "allow";
    return clause?.hostedAction?.[s] ?? DEFAULT_HOSTED_ACTION[s];
}
/** "removed before it reaches a public provider" — what happens to content bound for one, in words. */
export function hostedActionWords(action) {
    switch (action) {
        case "allow":
            return "may go to a public provider";
        case "report":
            return "goes to a public provider, and is reported";
        case "redact":
            return "removed before it reaches a public provider";
        case "block":
            return "stopped before it reaches a public provider";
    }
}
// ── Rules against a label ─────────────────────────────────────────────
const bareGuid = (id) => id.trim().replace(/^\{|\}$/g, "").toLowerCase();
/** The id or name a `label:` match names, or undefined when it is not one. */
export function labelMatchTarget(match) {
    if (typeof match !== "string" || !match.toLowerCase().startsWith(LABEL_MATCH_PREFIX))
        return undefined;
    const target = match.slice(LABEL_MATCH_PREFIX.length).trim();
    return target || undefined;
}
/** A label's name for comparing: no case, one space, a backslash between parent and child (a slash does too). */
const nameKey = (n) => n
    .trim()
    .toLowerCase()
    .replace(/\s*[\\/]\s*/g, " \\ ")
    .replace(/\s+/g, " ");
/**
 * Does a `label:<id or name>` match name this label? By its GUID (braces and case ignored), by
 * its own name, or by its full name with its parent's ("Confidential \ Project Falcon"; a slash
 * does as well as the backslash). A match naming only the child ("Project Falcon") matches the
 * full name too. Names compare without case.
 */
export function labelMatches(match, label) {
    const target = labelMatchTarget(match);
    if (!target)
        return false;
    if (bareGuid(target) === bareGuid(label.id))
        return true;
    const want = nameKey(target);
    return [label.name, ...(label.names ?? [])].some((n) => {
        if (!n)
            return false;
        const have = nameKey(n);
        return have === want || have.endsWith(` \\ ${want}`);
    });
}
const STRICT = { allow: 0, ask: 1, block: 2 };
/**
 * What the clause's rules say about content carrying `label` bound for `route`: the strictest
 * matching rule, or undefined when none matches (the sensitivity's routes decide). A rule never
 * applies to `local` unless it names it.
 */
export function labelRuleDecision(clause, label, route) {
    let best;
    (clause?.rules ?? []).forEach((r, i) => {
        const routes = r.routes ?? ["organisation", "hosted"];
        if (!routes.includes(route) || !labelMatches(r.match, label))
            return;
        if (!best || STRICT[r.action] > STRICT[best.action])
            best = { action: r.action, rule: i + 1 };
    });
    return best;
}
/** Does a tool rule's `match` (a `label:` condition) hold for a session that has seen `labels`? */
export function labelsMatch(match, labels) {
    return (labels ?? []).some((l) => labelMatches(match, l));
}
//# sourceMappingURL=sensitivityLabels.js.map